import {
  drugApi,
  type DrugFilter,
  type DrugSearchParams,
  type FeeBasis,
} from '@/entities/drug/api/drug.api';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';

export const drugKeys = {
  all: ['drugs'] as const,
  search: (params: DrugSearchParams) =>
    [...drugKeys.all, 'search', params] as const,
  suggestions: (keyword: string) =>
    [...drugKeys.all, 'suggestions', keyword] as const,
  pharmaCompanies: (keyword: string, filters: DrugFilter[]) =>
    [...drugKeys.all, 'pharma-companies', keyword, filters] as const,
  detail: (drugCode: string) => [...drugKeys.all, 'detail', drugCode] as const,
  related: (
    drugCode: string,
    feeBasis: FeeBasis,
    includeSameFormulationGroup: boolean,
  ) =>
    [
      ...drugKeys.all,
      'related',
      drugCode,
      feeBasis,
      includeSameFormulationGroup,
    ] as const,
};

/** 404 · DRUG_NOT_FOUND는 다시 시도해도 같으므로 재시도하지 않는다 */
const retryUnlessNotFound = (failureCount: number, error: unknown) =>
  !(axios.isAxiosError(error) && error.response?.status === 404) &&
  failureCount < 1;

export const isDrugNotFoundError = (error: unknown) =>
  axios.isAxiosError<{ code?: string }>(error) &&
  (error.response?.status === 404 ||
    error.response?.data?.code === 'DRUG_NOT_FOUND');

/** M-11 검색 결과 — 필터를 바꾸는 동안 이전 결과를 유지해 깜빡이지 않게 한다 */
export const useDrugSearch = (params: DrugSearchParams, enabled = true) =>
  useQuery({
    queryKey: drugKeys.search(params),
    queryFn: () => drugApi.search(params),
    enabled: enabled && params.keyword.trim().length > 0,
    placeholderData: keepPreviousData,
  });

/**
 * M-10 자동완성 — GET /suggestions. 두 글자 이상일 때만, 250ms 디바운스 후 호출한다.
 * 키워드가 바뀌면 react-query가 이전 요청을 abort한다 (signal).
 */
export const useDrugAutocomplete = (input: string) => {
  const keyword = useDebouncedValue(input.trim(), 250);
  const enabled = keyword.length >= 2;
  const query = useQuery({
    queryKey: drugKeys.suggestions(keyword),
    queryFn: ({ signal }) => drugApi.suggestions(keyword, signal),
    enabled,
    staleTime: 60 * 1000,
  });
  return { ...query, keyword, enabled };
};

/** 제약사 선택지 — 검색어 · 약품 필터가 바뀔 때만 다시 받는다 */
export const usePharmaCompanies = (keyword: string, filters: DrugFilter[]) =>
  useQuery({
    queryKey: drugKeys.pharmaCompanies(keyword, filters),
    queryFn: () => drugApi.pharmaCompanies(keyword, filters),
    enabled: keyword.trim().length > 0,
    placeholderData: keepPreviousData,
  });

/** M-12 상세 · M-15 호버 미리보기가 같은 캐시를 쓴다 */
export const useDrugDetail = (drugCode?: string) =>
  useQuery({
    queryKey: drugKeys.detail(drugCode ?? ''),
    queryFn: () => drugApi.detail(drugCode!),
    enabled: !!drugCode,
    retry: retryUnlessNotFound,
  });

/** M-12 동일 성분 — 비교 기준 · 제형군 포함을 바꾸면 서버에서 다시 받는다 */
export const useRelatedDrugs = (
  drugCode: string | undefined,
  feeBasis: FeeBasis,
  includeSameFormulationGroup: boolean,
) =>
  useQuery({
    queryKey: drugKeys.related(
      drugCode ?? '',
      feeBasis,
      includeSameFormulationGroup,
    ),
    queryFn: () =>
      drugApi.related(drugCode!, feeBasis, includeSameFormulationGroup),
    enabled: !!drugCode,
    placeholderData: keepPreviousData,
    retry: retryUnlessNotFound,
  });
